import { action, computed, makeObservable, observable, runInAction } from "mobx";
import {
    type LoginTokensApi,
    type EmailPasswordLoginInfo,
    ResponseError,
    type LoginToken,
} from "../../api";
import type { ServiceErrors } from "./service-errors";
import { routePath } from "../../route-path";
import {
    getLocalStorageItem,
    removeLocalStorageItem,
    setLocalStorageItem,
} from "../../utils/local-storage";
import type { HistoryProvider } from "../providers/history-provider";
import type { ServiceLoginToken, LoginTokenWithContext } from "./service-login-token";

export enum LoginStatus {
    SUCCESS = "success",
    INVALID = "invalid",
}

export interface LoginResultSuccess {
    readonly status: LoginStatus.SUCCESS;
    readonly loginToken: LoginToken;
}

export interface LoginResultInvalid {
    readonly status: LoginStatus.INVALID;
}

export type LoginResult = LoginResultSuccess | LoginResultInvalid;

export enum LogoutReason {
    USER_LOGOUT = "user",
    INVALID_TOKEN = "invalid",
    NOT_AUTHENTICATED = "not_authenticated",
    UNKNOWN = "unknown",
}

const LOGOUT_REASON_STORAGE_KEY = "logoutReason";

export function setLogoutReason(reason: LogoutReason): void {
    setLocalStorageItem(LOGOUT_REASON_STORAGE_KEY, reason);
}

export function consumeLogoutReason(): LogoutReason | undefined {
    const reason = getLocalStorageItem(LOGOUT_REASON_STORAGE_KEY);
    removeLocalStorageItem(LOGOUT_REASON_STORAGE_KEY);
    if (reason == null || !Object.values(LogoutReason).includes(reason as LogoutReason)) {
        return;
    }
    return reason as LogoutReason;
}

const EXPIRATION_CHECK_INTERVAL_IN_MS = 5 * 60 * 1000; // 5 minutes

export class ServiceAuth {
    @observable public accessor lastLoginResult: LoginStatus | undefined = undefined;

    constructor(
        private readonly serviceErrors: ServiceErrors,
        private readonly serviceLoginToken: ServiceLoginToken,
        private readonly loginTokensApi: LoginTokensApi,
        private readonly historyProvider: HistoryProvider,
    ) {
        makeObservable(this);

        window.addEventListener("unhandledrejection", (event) =>
            this.handleGlobalError(event.reason),
        );
        window.addEventListener("error", (error) => this.handleGlobalError(error.error));

        setInterval(
            () => this.handleCheckIfSessionShouldBeRefreshed(),
            EXPIRATION_CHECK_INTERVAL_IN_MS,
        );
    }

    @action private handleCheckIfSessionShouldBeRefreshed(): void {
        if (this.loginToken != null && this.loginToken.context === "sso") {
            const started = new Date(this.loginToken.createdAt);
            const expiration = new Date(this.loginToken.expiry);

            const halfwayThreshold =
                started.getTime() + (expiration.getTime() - started.getTime()) / 2;

            if (Date.now() > halfwayThreshold) {
                this.refreshSsoSession();
            }
        }
    }

    @action private handleGlobalError(error: unknown): void {
        if (error instanceof ResponseError && error.response.status === 401) {
            this.invalidateSession(error.response);
        }
    }

    /** Check whether the user is logged in. */
    @computed public get isLoggedIn(): boolean {
        return this.loginToken !== undefined;
    }

    /** Retrieve stored login token. */
    @computed public get loginToken(): LoginTokenWithContext | undefined {
        return this.serviceLoginToken.loginToken;
    }

    /** Attempt to login using the provided credentials. The token will be stored. */
    @action.bound public async login(info: EmailPasswordLoginInfo): Promise<LoginResult> {
        if (this.isLoggedIn) {
            throw new Error("Already signed in. Cannot sign in again.");
        }
        try {
            const loginToken = await this.loginTokensApi.loginTokensCreateLoginToken({
                authenticationInfo: {
                    emailPassword: info,
                },
            });
            runInAction(() => {
                this.serviceLoginToken.setLoginToken(loginToken);
                this.lastLoginResult = LoginStatus.SUCCESS;
            });
            return { status: LoginStatus.SUCCESS, loginToken };
        } catch (error) {
            if (!(error instanceof ResponseError)) {
                this.serviceErrors.reportError({
                    title: "component.errorBar.auth.login.unknownReason.title",
                    description: "component.errorBar.auth.login.unknownReason.description",
                    error: error as Error,
                    fatal: true,
                });
            } else if (error.response.status !== 401) {
                this.serviceErrors.reportResponse(error.response);
            }
            runInAction(() => (this.lastLoginResult = LoginStatus.INVALID));
            return { status: LoginStatus.INVALID };
        }
    }

    /** Attempt to login using the provided email login token. The token will be stored. */
    @action.bound public async loginWithEmailLoginToken(
        emailLoginToken: string,
    ): Promise<LoginResult> {
        if (this.isLoggedIn) {
            throw new Error("Already signed in. Cannot sign in again.");
        }
        try {
            const loginToken = await this.loginTokensApi.loginTokensCreateLoginToken({
                authenticationInfo: {
                    emailLoginToken,
                },
            });
            runInAction(() => {
                this.serviceLoginToken.setLoginToken(loginToken);
                this.lastLoginResult = LoginStatus.SUCCESS;
            });
            return { status: LoginStatus.SUCCESS, loginToken };
        } catch (error) {
            if (!(error instanceof ResponseError)) {
                this.serviceErrors.reportError({
                    title: "component.errorBar.auth.login.unknownReason.title",
                    description: "component.errorBar.auth.login.unknownReason.description",
                    error: error as Error,
                    fatal: true,
                });
            } else if (error.response.status !== 401) {
                this.serviceErrors.reportResponse(error.response);
            }
            runInAction(() => (this.lastLoginResult = LoginStatus.INVALID));
            return { status: LoginStatus.INVALID };
        }
    }

    /** Sign out by invalidating the token on the backend and clearing it from the local storage. */
    @action.bound public async logout(reason: LogoutReason): Promise<void> {
        if (!this.loginToken) {
            throw new Error("Not signed in. Cannot sign out.");
        }
        try {
            await this.loginTokensApi.loginTokensDeleteLoginToken({
                loginTokenId: this.loginToken.id,
            });
            setLogoutReason(reason);
            this.serviceLoginToken.setLoginToken(undefined);
            // Reload the page to completely clear all cached data
            window.location.href = routePath.login();
        } catch (error) {
            if (!(error instanceof ResponseError)) {
                this.serviceErrors.reportError({
                    title: "component.errorBar.auth.logout.unknownReason.title",
                    description: "component.errorBar.auth.logout.unknownReason.description",
                    error: error as Error,
                });
            } else {
                this.serviceErrors.reportResponse(error.response);
            }
        }
    }

    /**
     * Sign out the user because their token expired.
     * Report the failed request as well to allow diagnosing issues in tests.
     */
    @action.bound public invalidateSession(error?: Response): void {
        this.serviceErrors.reportError({
            title: "component.errorBar.auth.invalid.title",
            description: "component.errorBar.auth.invalid.description",
            error,
        });
        setLogoutReason(LogoutReason.INVALID_TOKEN);
        this.serviceLoginToken.setLoginToken(undefined);

        // For privacy reasons and because we treat the internal state as completely broken at
        // this point.
        this.historyProvider.history.push(routePath.login(), {
            from: this.historyProvider.history.location,
        });
    }

    /**
     * Attempt to refresh login token backed by OpenID Connect session
     */
    @action.bound public async refreshSsoSession(): Promise<void> {
        if (!this.loginToken) {
            throw new Error("Not signed in. Cannot refresh token.");
        }

        if (this.loginToken.context !== "sso") {
            throw new Error("Only SSO login tokens can be refreshed");
        }

        try {
            const loginToken = await this.loginTokensApi.loginTokensRefresh();
            runInAction(() => {
                this.serviceLoginToken.setLoginToken({
                    ...loginToken,
                    context: this.loginToken?.context,
                });
            });
        } catch (error) {
            if (!(error instanceof ResponseError)) {
                this.serviceErrors.reportError({
                    title: "component.errorBar.auth.logout.unknownReason.title",
                    description: "component.errorBar.auth.logout.unknownReason.description",
                    error: error as Error,
                });
            } else {
                this.serviceErrors.reportResponse(error.response);
            }
        }
    }
}
